import { useEffect, useMemo, useState } from 'react';
import { Control, FieldValues, Path, useWatch } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const getRules = (translations?: {
  minLength: string;
  uppercase: string;
  lowercase: string;
  number: string;
  special: string;
}) => [
  {
    id: 1,
    test: (p: string) => p.length >= 8,
    description: translations?.minLength || 'Your password must be at least 8 characters long',
  },
  {
    id: 2,
    test: (p: string) => /[A-Z]/.test(p),
    description: translations?.uppercase || 'Include at least one uppercase letter (A-Z)',
  },
  {
    id: 3,
    test: (p: string) => /[a-z]/.test(p),
    description: translations?.lowercase || 'Include at least one lowercase letter (a-z)',
  },
  {
    id: 4,
    test: (p: string) => /[0-9]/.test(p),
    description: translations?.number || 'Include at least one number (0-9)',
  },
  {
    id: 5,
    test: (p: string) => /[^A-Za-z0-9]/.test(p),
    description: translations?.special || 'Include at least one special character (!@#$%^&*)',
  },
];

export function PasswordRequirements<T extends FieldValues>({
  control,
  watch,
}: {
  control: Control<T>;
  watch: Path<T>;
}) {
  const password = useWatch({ control, name: watch });
  const [currentRule, setCurrentRule] = useState<number>(0);
  const { t, i18n } = useTranslation();

  const rules = useMemo(
    () => getRules(t('auth.register.passwordRequirements.rules', { returnObjects: true })),
    [t, i18n.language]
  );

  const allRulesMet = password ? rules.every((rule) => rule.test(password)) : false;

  useEffect(() => {
    if (!password) {
      setCurrentRule(0);
      return;
    }

    const unmetRules = rules.filter((rule) => !rule.test(password));
    if (unmetRules.length > 0) {
      const currentUnmetRule = unmetRules[0];
      setCurrentRule(currentUnmetRule.id);
    } else {
      setCurrentRule(-1);
    }
  }, [password]);

  return (
    <motion.div
      className="mt-2 overflow-hidden"
      initial={false}
      animate={{
        height: password ? 'auto' : 0,
      }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
    >
      <AnimatePresence mode="wait">
        {password && !allRulesMet && currentRule !== -1 && (
          <motion.div
            key={currentRule}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1 text-sm text-amber-600"
          >
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex flex-col">
              <span className="text-xs text-gray-500">
                {rules.find((r) => r.id === currentRule)?.description}
              </span>
            </div>
          </motion.div>
        )}
        {allRulesMet && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1 text-sm text-green-600"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4" />
            <span className="text-xs font-medium">
              {t('auth.register.passwordRequirements.meetsRequirements')}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
